import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { CheckCircle, Phone, Home, Lightbulb, Plug, Shield } from 'lucide-react';

const ResidentialElectrical = () => {
  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-blue-900 to-blue-700 text-white py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl">
            <h1 className="text-4xl md:text-5xl font-bold mb-6">
              Residential Electrical Services
            </h1>
            <p className="text-xl text-blue-100 mb-8">
              Professional residential electrical services for homeowners in London, Kingston, and surrounding areas. From simple repairs to complete rewiring, we handle all your home electrical needs.
            </p>
            <div className="flex flex-wrap gap-4">
              <Button asChild size="lg" className="bg-yellow-500 hover:bg-yellow-600 text-gray-900 font-semibold">
                <Link to="/contact">Schedule Service</Link>
              </Button>
              <Button asChild size="lg" variant="outline" className="border-2 border-white text-white hover:bg-white hover:text-blue-900">
                <a href="tel:519-709-9242">
                  <Phone className="mr-2 h-5 w-5" />
                  Call 519-709-9242
                </a>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Services Overview */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                Complete Home Electrical Solutions
              </h2>
              <p className="text-lg text-gray-600 mb-6">
                At Studerus Electric Inc., we understand that your home's electrical system is crucial to your comfort, safety, and daily life. Our licensed electricians provide reliable, code-compliant electrical services for all your residential needs.
              </p>
              <p className="text-lg text-gray-600 mb-6">
                Whether you need a simple outlet installation or a complete home rewiring, our experienced team delivers quality workmanship with same-day and next-day service options available.
              </p>
              <div className="space-y-3">
                <div className="flex items-center gap-3">
                  <CheckCircle className="h-6 w-6 text-green-600 flex-shrink-0" />
                  <p className="text-gray-700">Licensed & Insured Electricians</p>
                </div>
                <div className="flex items-center gap-3">
                  <CheckCircle className="h-6 w-6 text-green-600 flex-shrink-0" />
                  <p className="text-gray-700">Same & Next Day Service Available</p>
                </div>
                <div className="flex items-center gap-3">
                  <CheckCircle className="h-6 w-6 text-green-600 flex-shrink-0" />
                  <p className="text-gray-700">Code-Compliant Installations</p>
                </div>
                <div className="flex items-center gap-3">
                  <CheckCircle className="h-6 w-6 text-green-600 flex-shrink-0" />
                  <p className="text-gray-700">Upfront Pricing & Free Quotes</p>
                </div>
              </div>
            </div>
            <div>
              <img
                src="https://customer-assets.emergentagent.com/job_next-day-electric/artifacts/dlmhugk4_20220304_132833.jpg"
                alt="Residential electrical service"
                className="rounded-lg shadow-xl w-full"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 text-center mb-12">
            Our Residential Electrical Services
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <Card>
              <CardHeader>
                <Lightbulb className="h-12 w-12 text-yellow-500 mb-4" />
                <CardTitle>Lighting Installation & Repair</CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2 text-gray-600">
                  <li>• Interior & exterior lighting</li>
                  <li>• Recessed lighting</li>
                  <li>• LED upgrades</li>
                  <li>• Dimmer switches</li>
                  <li>• Landscape lighting</li>
                </ul>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <Plug className="h-12 w-12 text-blue-600 mb-4" />
                <CardTitle>Outlets & Switches</CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2 text-gray-600">
                  <li>• Outlet installation & repair</li>
                  <li>• GFCI outlets</li>
                  <li>• USB outlet installation</li>
                  <li>• Switch replacement</li>
                  <li>• Smart switches</li>
                </ul>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <Shield className="h-12 w-12 text-green-600 mb-4" />
                <CardTitle>Panel Upgrades</CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2 text-gray-600">
                  <li>• Electrical panel replacement</li>
                  <li>• Service upgrades (100A to 200A)</li>
                  <li>• Circuit breaker replacement</li>
                  <li>• Sub-panel installation</li>
                  <li>• Fuse box upgrades</li>
                </ul>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <Home className="h-12 w-12 text-purple-600 mb-4" />
                <CardTitle>Home Rewiring</CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2 text-gray-600">
                  <li>• Complete home rewiring</li>
                  <li>• Aluminum wiring replacement</li>
                  <li>• Knob & tube removal</li>
                  <li>• Code compliance upgrades</li>
                  <li>• Room additions</li>
                </ul>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <Plug className="h-12 w-12 text-red-600 mb-4" />
                <CardTitle>EV Charger Installation</CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2 text-gray-600">
                  <li>• Level 2 charger installation</li>
                  <li>• Dedicated circuit installation</li>
                  <li>• Indoor & outdoor chargers</li>
                  <li>• Panel capacity assessment</li>
                  <li>• Permit & inspection coordination</li>
                </ul>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <Shield className="h-12 w-12 text-blue-900 mb-4" />
                <CardTitle>Safety & Emergency</CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2 text-gray-600">
                  <li>• Smoke detector installation</li>
                  <li>• Carbon monoxide detectors</li>
                  <li>• Surge protection</li>
                  <li>• Emergency repairs</li>
                  <li>• Electrical troubleshooting</li>
                </ul>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <Home className="h-12 w-12 text-orange-600 mb-4" />
                <CardTitle>Kitchen & Bathroom</CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2 text-gray-600">
                  <li>• Appliance circuits</li>
                  <li>• Under-cabinet lighting</li>
                  <li>• Bathroom fan installation</li>
                  <li>• GFCI protection</li>
                  <li>• Heated floor wiring</li>
                </ul>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <Lightbulb className="h-12 w-12 text-indigo-600 mb-4" />
                <CardTitle>Smart Home Integration</CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2 text-gray-600">
                  <li>• Smart lighting systems</li>
                  <li>• Home automation wiring</li>
                  <li>• Smart thermostat installation</li>
                  <li>• Security system wiring</li>
                  <li>• Smart switch installation</li>
                </ul>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <Plug className="h-12 w-12 text-teal-600 mb-4" />
                <CardTitle>Outdoor Electrical</CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2 text-gray-600">
                  <li>• Outdoor outlet installation</li>
                  <li>• Hot tub wiring</li>
                  <li>• Pool electrical</li>
                  <li>• Landscape lighting</li>
                  <li>• Weatherproof installations</li>
                </ul>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 text-center mb-12">
            Why Choose Studerus Electric?
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 max-w-6xl mx-auto">
            <div className="text-center">
              <div className="bg-blue-900 text-white rounded-full w-20 h-20 flex items-center justify-center text-3xl font-bold mx-auto mb-4">
                17+
              </div>
              <h3 className="font-semibold text-lg mb-2">Team of Electricians</h3>
              <p className="text-gray-600">Large team ready to handle any project size</p>
            </div>
            <div className="text-center">
              <div className="bg-yellow-500 text-gray-900 rounded-full w-20 h-20 flex items-center justify-center text-3xl font-bold mx-auto mb-4">
                24h
              </div>
              <h3 className="font-semibold text-lg mb-2">Fast Response</h3>
              <p className="text-gray-600">Same & next day service available</p>
            </div>
            <div className="text-center">
              <div className="bg-green-600 text-white rounded-full w-20 h-20 flex items-center justify-center text-3xl font-bold mx-auto mb-4">
                ✓
              </div>
              <h3 className="font-semibold text-lg mb-2">Licensed & Insured</h3>
              <p className="text-gray-600">Fully certified Ontario electricians</p>
            </div>
            <div className="text-center">
              <div className="bg-blue-600 text-white rounded-full w-20 h-20 flex items-center justify-center text-3xl font-bold mx-auto mb-4">
                $
              </div>
              <h3 className="font-semibold text-lg mb-2">Transparent Pricing</h3>
              <p className="text-gray-600">Upfront quotes with no hidden fees</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-r from-blue-900 to-blue-700 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Need Residential Electrical Services?
          </h2>
          <p className="text-xl text-blue-100 mb-8 max-w-2xl mx-auto">
            Contact us today for reliable, professional electrical services for your home
          </p>
          <div className="flex flex-wrap gap-4 justify-center">
            <Button asChild size="lg" className="bg-yellow-500 hover:bg-yellow-600 text-gray-900 font-semibold">
              <Link to="/contact">Get Free Quote</Link>
            </Button>
            <Button asChild size="lg" variant="outline" className="border-2 border-white text-white hover:bg-white hover:text-blue-900">
              <a href="tel:519-709-9242">
                <Phone className="mr-2 h-5 w-5" />
                Call 519-709-9242
              </a>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ResidentialElectrical;
